/* eslint-disable no-undef */
import { useEffect, useState } from "react";
import { GoogleMap, LoadScript, OverlayView } from "@react-google-maps/api";
import {
  MarkerClusterer,
  Renderer,
  Cluster,
} from "@googlemaps/markerclusterer";
import styles from "./custom-map.module.css";

// Styling for the map container
const mapContainerStyle = {
  width: "100%",
  height: "400px",
};

const sampleLogo = "assets/images/sample-school.jpg";

function createCustomIcon(logoUrl, borderWidth = 5, size = 40) {
  // Create a canvas element
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  // Set the canvas size to include the tail
  canvas.width = size;
  canvas.height = size + size / 6; // Height of the square plus the tail

  // Round the corners of the square
  const cornerRadius = 5; // Control the roundness of the corners

  ctx.beginPath();
  ctx.moveTo(cornerRadius, 0); // Start from the top-left corner with rounding
  ctx.lineTo(size - cornerRadius, 0); // Top edge
  ctx.arcTo(size, 0, size, cornerRadius, cornerRadius); // Top-right corner
  ctx.lineTo(size, size - cornerRadius); // Right edge
  ctx.arcTo(size, size, size - cornerRadius, size, cornerRadius); // Bottom-right corner
  ctx.lineTo(cornerRadius, size); // Bottom edge
  ctx.arcTo(0, size, 0, size - cornerRadius, cornerRadius); // Bottom-left corner
  ctx.lineTo(0, cornerRadius); // Left edge
  ctx.arcTo(0, 0, cornerRadius, 0, cornerRadius); // Top-left corner
  ctx.closePath();

  // Fill the square with white color (border area)
  ctx.fillStyle = "white";
  ctx.fill();

  // Create an image element for the logo
  const img = new Image();
  img.crossOrigin = 'Anonymous';
  img.src = logoUrl;

  // Return a promise that resolves when the image is loaded
  return new Promise((resolve) => {
    img.onload = () => {
      // Draw the logo centered inside the square (with rounded corners)
      const logoSize = size - borderWidth * 2;
      ctx.drawImage(
        img,
        borderWidth, // X position
        borderWidth, // Y position
        logoSize, // Width of the logo
        logoSize // Height of the logo
      );

      // Draw the pin tail at the bottom of the square
      const tailHeight = size / 4; // Height of the tail
      const tailWidth = size / 6; // Width of the tail (half the square width)

      ctx.beginPath();
      ctx.moveTo(size / 2 - tailWidth / 2, size); // Left point of the triangle (bottom-left)
      ctx.lineTo(size / 2 + tailWidth / 2, size); // Right point of the triangle (bottom-right)
      ctx.lineTo(size / 2, size + tailHeight); // Tip of the triangle (tail)
      ctx.closePath();

      // Fill the tail with white color
      ctx.fillStyle = "white";
      ctx.fill();

      // Convert canvas to base64 image URL
      const iconUrl = canvas.toDataURL();
      resolve(iconUrl); // Return the icon URL
    };
  });
}

export function CustomClientMap({ markersData, center }) {
  const [map, setMap] = useState(null);
  const [infoWindow, setInfoWindow] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false); // Track if Google Maps API is loaded

  // Callback when map is loaded
  const onMapLoad = (mapInstance) => {
    setMap(mapInstance);
  };

  // Handle marker click: show InfoWindow with name and address
  const onMarkerClick = (marker, record) => {
    const position = marker.getPosition();
    if (position) {
      const geocoder = new google.maps.Geocoder();
      // Perform reverse geocoding to get the address
      geocoder.geocode({ location: position }, (results, status) => {
        if (status === google.maps.GeocoderStatus.OK && results?.[0]) {
          const address = results[0].formatted_address;

          // Create the content for the InfoWindow
          const contentString = `
          <div style="min-width: 150px; min-height: 50px; border-top: 1px solid #aaa; padding: 10px; display: flex; flex-direction: column;gap: 10px">
            <div style="display: flex; gap:5px; justify-content: 'center">
              <img src=${
                record?.logo ?? sampleLogo
              } alt="Logo" style="width: 26px; height: 26px;" />
              <span style="color: #171717;font-size: 14px;font-style: normal;font-weight: 500;padding-top: 5px;">${
                record?.institution_name
              }</span>
            </div>
            <span style="color: #4A4A4A;font-size: 12px;font-style: normal;font-weight: 400;">${address}</span>
            <span style="color: #171717;font-size: 12px;font-style: normal;font-weight: 400;">Total Students: ${
              record?.totalStudents
            }</span>
          </div>
        `;

          // Close the previous InfoWindow if open
          if (infoWindow) {
            infoWindow.close();
          }

          // Create a new InfoWindow and open it
          const newInfoWindow = new google.maps.InfoWindow({
            content: contentString,
            position: position, // Set InfoWindow position
          });

          newInfoWindow.open(map, marker);
          setInfoWindow(newInfoWindow); // Set the new InfoWindow to state
        } else {
          alert(
            "Geocode was not successful for the following reason: " + status
          );
        }
      });
    }
  };

  // Custom renderer for the cluster
  const clusterRenderer = {
    render: (cluster) => {
      const count = cluster.markers ? cluster.markers.length : 0;

      // Create a custom marker for the cluster
      const clusterMarker = new google.maps.Marker({
        position: cluster.position, // The cluster's center position
        label: {
          text: count.toString(),
          color: "white",
          fontSize: "16px",
          fontWeight: "600",
        },
        icon: {
          path: `
            M -8.5,-10 
            Q -10,-10 -10,-8.5 
            L -10,8 
            Q -10,10 -8.5,10 
            L -3,10 

            Q 0,18 3,10

            L 8.5,10 
            Q 10,10 10,8.5 
            L 10,-8.5 
            Q 10,-10 8.5,-10 
            Z
          `,
          fillColor: "#39B54A", // Green background
          fillOpacity: 1,
          strokeColor: "#FFFFFF", // White border
          strokeWeight: 5, // 3px border
          scale: 2,
          anchor: new google.maps.Point(0, 0),
        },
      });

      return clusterMarker;
    },
  };

  // Create markers and clusters when the map is loaded
  useEffect(() => {
    const groupedData = markersData.reduce(
      (acc, { latitude, longitude, institution_name, totalStudents, logo }) => {
        const key = `${latitude},${longitude}`; // Unique key for each coordinate pair
        if (!acc[key]) {
          acc[key] = { latitude, longitude, institution_name, totalStudents, logo };
        }
        return acc;
      },
      {}
    );

    const result = Object.values(groupedData);

    if (map && window.google && window.google.maps) {
      // Use Promise.all to ensure that all markers are created before being passed to MarkerClusterer
      const createMarkers = async () => {
        const markers = await Promise.all(
          result.map(async (location) => {
            const modifiedLocation = {
              lat: Number(location?.latitude),
              lng: Number(location?.longitude),
            };

            const modifiedLogo = location?.logo ?? sampleLogo;

            // Wait for the custom icon URL to be created
            const iconUrl = await createCustomIcon(modifiedLogo, 5, 45);

            const marker = new google.maps.Marker({
              position: modifiedLocation,
              title: location?.institution_name,
              icon: {
                url: iconUrl,
                scaledSize: new google.maps.Size(50, 50),
              },
            });

            marker.addListener("click", () => onMarkerClick(marker, location));

            return marker;
          })
        );

        // Once all markers are created, add them to MarkerClusterer
        new MarkerClusterer({
          map: map,
          markers: markers,
          renderer: clusterRenderer, // Use the custom renderer here
        });
      };

      // Call the async function to create markers
      createMarkers();

      // Add click listener to close InfoWindow if clicked outside
      google.maps.event.addListener(map, "click", () => {
        if (infoWindow) {
          infoWindow.close();
          setInfoWindow(null);
        }
      });
    }
  }, [map, isLoaded, markersData, infoWindow, clusterRenderer]);

  return (
    <LoadScript
      googleMapsApiKey={process.env.REACT_APP_GOOGLE_API_KEY} // Your API key here
      onLoad={() => setIsLoaded(true)} // Set loaded state to true when script is loaded
      onError={(error) => console.error("Error loading Google Maps API", error)} // Handle loading errors
      libraries={["places"]}
    >
      <GoogleMap
        id="google-map"
        mapContainerStyle={mapContainerStyle}
        zoom={7}
        center={center}
        onLoad={onMapLoad} // Set map instance when map is loaded
      ></GoogleMap>
    </LoadScript>
  );
}

export default CustomClientMap;
